import { accountApi } from '@/api'
import { useAuth } from '@/composables/auth/useAuth'
import { useMainStore } from '@/stores/main'

const TG_WEBAPP_READY_TIMEOUT_MS = 2000
const TG_WEBAPP_POLL_INTERVAL_MS = 50
// Telegram Login Widget callback 在 popup 被 block / 注入失敗時可能永不觸發
// （官方未保證 callback 一定會呼叫），避免 re-entry lock 永久卡死
const TG_WIDGET_CALLBACK_TIMEOUT_MS = 120000

// Widget callback: 頂層含 id / auth_date
// WebApp initDataUnsafe: 含 user.id / auth_date
// WebApp initData (signed): URL-encoded string
const isValidTelegramAuthData = (data) => {
  if (typeof data === 'string') return data.length > 0
  if (!data || typeof data !== 'object') return false
  if (typeof data.id !== 'undefined' && typeof data.auth_date !== 'undefined') return true
  if (data.user && typeof data.user.id !== 'undefined') return true
  return false
}

const isValidWebAppInitData = (data) => {
  return data !== null && typeof data === 'object' && Object.keys(data).length > 0
}

export default defineNuxtPlugin(() => {
  const { $request } = useNuxtApp()
  const config = useRuntimeConfig()
  const store = useMainStore()
  const auth = useAuth()

  let isInFlight = false

  const beginFlight = () => {
    if (isInFlight) return false
    isInFlight = true
    store.setLoading({ thirdPartyLogin: true })
    return true
  }

  const endFlight = () => {
    isInFlight = false
    store.setLoading({ thirdPartyLogin: false })
  }

  function toggleButton(buttonType, action) {
    const { Telegram } = window || {}
    const tgWebApp = Telegram?.WebApp || {}
    const tgButton = tgWebApp[buttonType] || {}
    if (action === 'show') {
      tgButton.show()
    } else if (action === 'hide') {
      tgButton.hide()
    }
  }

  function setButtonClickEvt(buttonType, event) {
    const { Telegram } = window || {}
    const tgWebApp = Telegram?.WebApp || {}
    const tgButton = tgWebApp[buttonType] || {}
    tgButton.onClick(event)
  }

  function requestFullScree() {
    const { Telegram } = window || {}
    const tgWebApp = Telegram?.WebApp || {}
    tgWebApp.requestFullscreen()
  }

  async function waitForTelegramWebApp() {
    const start = Date.now()
    while (Date.now() - start < TG_WEBAPP_READY_TIMEOUT_MS) {
      const tgWebApp = window?.Telegram?.WebApp
      if (tgWebApp && (isValidWebAppInitData(tgWebApp.initDataUnsafe) || tgWebApp.initData)) {
        return tgWebApp
      }
      await new Promise((resolve) => setTimeout(resolve, TG_WEBAPP_POLL_INTERVAL_MS))
    }
    return null
  }

  async function bindingTelegram(loginType, authData, isSignature) {
    if (!isValidTelegramAuthData(authData)) {
      console.warn('[Telegram] bindingTelegram aborted: invalid authData', authData)
      return
    }

    try {
      await $request.send(
        accountApi.applyBinding({
          provider: 'telegram',
          loginType,
          additionalUserInfo: authData,
          isSignature,
        })
      )

      window.dispatchEvent(new CustomEvent('third-party-bound'))
    } catch (error) {
      console.error('Telegram Bind Fail', error)
    }
  }

  async function callTelegramLogin(loginType, authData, isSignature) {
    if (!isValidTelegramAuthData(authData)) {
      console.warn('[Telegram] callTelegramLogin aborted: invalid authData', authData)
      return
    }

    try {
      await auth.loginWithTelegram({ loginType, additionalUserInfo: authData, isSignature })
    } catch (error) {
      console.error('Telegram Login Fail !', error)
    }
  }

  async function handleWebAppFlow(isBinding) {
    const tgWebApp = await waitForTelegramWebApp()
    if (!tgWebApp) {
      console.error('[Telegram] WebApp init data unavailable after timeout')
      return
    }

    const { initDataUnsafe, initData } = tgWebApp
    const useSignedData = Boolean(initDataUnsafe?.signature)
    const authData = useSignedData ? initData : initDataUnsafe

    if (isBinding) {
      await bindingTelegram('WebApp', authData, useSignedData)
    } else {
      await callTelegramLogin('WebApp', authData, useSignedData)
    }
  }

  function handleWidgetFlow(isBinding) {
    const TELEGRAM_BOT_TOKEN = config.public.telegramBotToken
    if (!TELEGRAM_BOT_TOKEN) {
      console.error('[Telegram] Bot token missing, cannot init Widget login')
      endFlight()
      return
    }

    const bot_id = TELEGRAM_BOT_TOKEN.split(':')[0]
    const tgLoginApi = window?.Telegram?.Login
    if (!tgLoginApi?.auth) {
      console.error('[Telegram] Login widget script not loaded')
      endFlight()
      return
    }

    let settled = false
    const safetyTimer = setTimeout(() => {
      if (settled) return
      settled = true
      console.warn('[Telegram] Widget callback did not fire within timeout, releasing lock')
      endFlight()
    }, TG_WIDGET_CALLBACK_TIMEOUT_MS)

    tgLoginApi.auth({ bot_id, request_access: 'write', embed: 1 }, async (user) => {
      if (settled) return
      settled = true
      clearTimeout(safetyTimer)

      try {
        if (!isValidTelegramAuthData(user)) {
          // user === false 為使用者取消 / popup blocker / 逾時的 Telegram 官方行為
          return
        }

        if (isBinding) {
          await bindingTelegram('Widget', user, false)
        } else {
          await callTelegramLogin('Widget', user, false)
        }
      } finally {
        endFlight()
      }
    })
  }

  async function tgLogin(loginType, isBinding = false) {
    if (!beginFlight()) {
      console.warn('[Telegram] tgLogin ignored: another login is in flight')
      return
    }

    try {
      if (loginType === 'WebApp') {
        try {
          await handleWebAppFlow(isBinding)
        } finally {
          endFlight()
        }
        return
      }

      if (loginType === 'Widget') {
        // Widget 走非同步 callback，endFlight 在 callback 內觸發
        handleWidgetFlow(isBinding)
        return
      }

      console.error('[Telegram] Unknown loginType:', loginType)
      endFlight()
    } catch (error) {
      console.error('[Telegram] tgLogin unexpected error', error)
      endFlight()
    }
  }

  return {
    provide: {
      tgWebAppShowBtn: (buttonType) => toggleButton(buttonType, 'show'),
      tgWebAppHideBtn: (buttonType) => toggleButton(buttonType, 'hide'),
      tgWebAppSetBtnEvt: (buttonType, event) => setButtonClickEvt(buttonType, event),
      tgWebAppFullScreen: () => requestFullScree(),
      tgLogin,
    },
  }
})
